/**
 * API client for Z-Image Generator backend
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "";

// Token storage key
const TOKEN_KEY = "z_image_token";

export interface GenerateRequest {
  prompt: string;
  width?: number;
  height?: number;
  seed?: number | null;
  num_inference_steps?: number;
}

export interface GenerateResponse {
  image_id: string;
  filename: string;
  seed: number;
  width: number;
  height: number;
  generation_time: number;
  prompt: string;
  image_url: string;
  video_id?: string | null;
  video_status?: string | null;
  is_uploaded?: boolean;
  is_edited?: boolean;
  source_edit_id?: string | null;
}

// Video types
export interface VideoGenerateRequest {
  image_id: string;
  prompt?: string;
}

export interface VideoGenerateResponse {
  video_id: string;
  status: string;
  estimated_time: number;
}

export interface VideoStatusResponse {
  video_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  progress?: number;
  generation_time?: number;
  error_message?: string;
  video_url?: string;
}

export interface VideoResponse {
  video_id: string;
  filename?: string;
  prompt: string;
  status: string;
  duration?: number;
  generation_time?: number;
  error_message?: string;
  created_at?: string;
  completed_at?: string;
  video_url?: string;
  source_image_id: string;
}

export interface VideoListResponse {
  videos: VideoResponse[];
  total: number;
}

// Edit types
export interface EditGenerateRequest {
  image_id: string;
  edit_prompt: string;
}

export interface EditGenerateResponse {
  edit_id: string;
  status: string;
  estimated_time: number;
}

export interface EditStatusResponse {
  edit_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  progress?: number;
  generation_time?: number;
  error_message?: string;
  result_image_id?: string;
}

export interface ImageListResponse {
  images: GenerateResponse[];
  total: number;
}

export interface HealthResponse {
  status: string;
  model_loaded: boolean;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

export interface UserResponse {
  id: number;
  email: string;
  name: string;
  role: "pending" | "user" | "admin" | "suspended" | "banned";
}

// Admin API types
export interface AdminStats {
  total_users: number;
  pending_users: number;
  active_users: number;
  suspended_users: number;
  banned_users: number;
  admin_users: number;
  total_images: number;
  total_videos: number;
}

export interface AdminUserListItem {
  id: number;
  email: string;
  name: string;
  role: string;
  created_at: string;
  approved_at?: string;
  suspended_at?: string;
  suspended_reason?: string;
  image_count: number;
  video_count: number;
}

export interface AdminUserListResponse {
  users: AdminUserListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminUserDetail {
  id: number;
  email: string;
  name: string;
  role: string;
  created_at: string;
  approved_at?: string;
  approved_by?: number;
  approver_name?: string;
  suspended_at?: string;
  suspended_reason?: string;
  image_count: number;
  video_count: number;
}

export interface AdminUserContent {
  user_id: number;
  user_name: string;
  images: {
    image_id: string;
    prompt: string;
    width: number;
    height: number;
    created_at: string;
    image_url: string;
  }[];
  videos: {
    video_id: string;
    prompt: string;
    status: string;
    created_at: string;
    video_url?: string;
  }[];
  total_images: number;
  total_videos: number;
}

// Token management
export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(TOKEN_KEY);
}

// Auth headers helper
function getAuthHeaders(): HeadersInit {
  const token = getToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// Authentication API
export async function register(
  email: string,
  name: string,
  password: string
): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, name, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Registration failed");
  }

  const data = await response.json();
  setToken(data.access_token);
  return data;
}

export async function login(
  email: string,
  password: string
): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Login failed");
  }

  const data = await response.json();
  setToken(data.access_token);
  return data;
}

export async function getCurrentUser(): Promise<UserResponse | null> {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        removeToken();
        return null;
      }
      throw new Error("Failed to get user info");
    }

    return response.json();
  } catch {
    return null;
  }
}

export function logout(): void {
  removeToken();
}

// Image generation API
export async function generateImage(
  request: GenerateRequest
): Promise<GenerateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/generate`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) {
      removeToken();
      throw new Error("Please login to generate images");
    }
    throw new Error(error.detail || "Failed to generate image");
  }

  return response.json();
}

export async function uploadImage(file: File): Promise<GenerateResponse> {
  const formData = new FormData();
  formData.append("file", file);

  const token = getToken();
  const headers: HeadersInit = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  // Note: Do NOT set Content-Type header - browser will set it with boundary for FormData

  const response = await fetch(`${API_BASE_URL}/api/images/upload`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) {
      removeToken();
      throw new Error("Please login to upload images");
    }
    throw new Error(error.detail || "Failed to upload image");
  }

  return response.json();
}

export async function getImageList(
  limit: number = 50,
  offset: number = 0
): Promise<ImageListResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/images?limit=${limit}&offset=${offset}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      throw new Error("Please login to view images");
    }
    throw new Error("Failed to fetch images");
  }

  return response.json();
}

export async function deleteImage(imageId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/images/${imageId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to delete image");
  }
}

export async function checkHealth(): Promise<HealthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/health`);
  return response.json();
}

export function getImageUrl(imageId: string): string {
  const token = getToken();
  // For authenticated requests, we'll need to handle this differently
  // For now, return the base URL (images require auth via header)
  return `${API_BASE_URL}/api/images/${imageId}`;
}

export function getImageUrlWithAuth(imageId: string): string {
  // This returns the URL with token as query param for img src usage
  const token = getToken();
  const baseUrl = `${API_BASE_URL}/api/images/${imageId}`;
  return token ? `${baseUrl}?token=${token}` : baseUrl;
}

export function downloadImage(imageId: string, filename?: string): void {
  const token = getToken();

  // Fetch with auth header and download
  fetch(getImageUrl(imageId), {
    headers: getAuthHeaders(),
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `${imageId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
}

// Video generation API
export async function generateVideo(
  request: VideoGenerateRequest
): Promise<VideoGenerateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/videos/generate`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) {
      removeToken();
      throw new Error("Please login to generate videos");
    }
    throw new Error(error.detail || "Failed to generate video");
  }

  return response.json();
}

export async function getVideoStatus(
  videoId: string
): Promise<VideoStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/api/videos/${videoId}/status`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get video status");
  }

  return response.json();
}

export async function getVideoList(
  limit: number = 50,
  offset: number = 0
): Promise<VideoListResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/videos?limit=${limit}&offset=${offset}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      throw new Error("Please login to view videos");
    }
    throw new Error("Failed to fetch videos");
  }

  return response.json();
}

export async function deleteVideo(videoId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/videos/${videoId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to delete video");
  }
}

export function getVideoUrl(videoId: string): string {
  return `${API_BASE_URL}/api/videos/${videoId}`;
}

export function getVideoUrlWithAuth(videoId: string): string {
  const token = getToken();
  const baseUrl = `${API_BASE_URL}/api/videos/${videoId}`;
  return token ? `${baseUrl}?token=${token}` : baseUrl;
}

export function downloadVideo(videoId: string, filename?: string): void {
  fetch(getVideoUrl(videoId), {
    headers: getAuthHeaders(),
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `${videoId}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
}

// ============== Image Edit API ==============

export async function generateEdit(
  request: EditGenerateRequest
): Promise<EditGenerateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/edits/generate`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) {
      removeToken();
      throw new Error("Please login to edit images");
    }
    throw new Error(error.detail || "Failed to start image edit");
  }

  return response.json();
}

export async function getEditStatus(
  editId: string
): Promise<EditStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/api/edits/${editId}/status`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get edit status");
  }

  return response.json();
}

// ============== Admin API ==============

export async function getAdminStats(): Promise<AdminStats> {
  const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch admin stats");
  }

  return response.json();
}

export async function getAdminUsers(
  role?: string,
  page: number = 1,
  pageSize: number = 20
): Promise<AdminUserListResponse> {
  const params = new URLSearchParams();
  if (role) params.append("role", role);
  params.append("page", String(page));
  params.append("page_size", String(pageSize));

  const response = await fetch(
    `${API_BASE_URL}/api/admin/users?${params.toString()}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch users");
  }

  return response.json();
}

export async function getAdminPendingUsers(
  page: number = 1,
  pageSize: number = 20
): Promise<AdminUserListResponse> {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("page_size", String(pageSize));

  const response = await fetch(
    `${API_BASE_URL}/api/admin/users/pending?${params.toString()}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch pending users");
  }

  return response.json();
}

export async function getAdminUserDetail(
  userId: number
): Promise<AdminUserDetail> {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch user details");
  }

  return response.json();
}

export async function getAdminUserContent(
  userId: number,
  page: number = 1,
  pageSize: number = 20
): Promise<AdminUserContent> {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("page_size", String(pageSize));

  const response = await fetch(
    `${API_BASE_URL}/api/admin/users/${userId}/content?${params.toString()}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch user content");
  }

  return response.json();
}

export async function approveUser(userId: number): Promise<{ message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/users/${userId}/approve`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to approve user");
  }

  return response.json();
}

export async function suspendUser(
  userId: number,
  reason: string
): Promise<{ message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/users/${userId}/suspend`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to suspend user");
  }

  return response.json();
}

export async function unsuspendUser(
  userId: number
): Promise<{ message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/users/${userId}/unsuspend`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to unsuspend user");
  }

  return response.json();
}

export async function banUser(
  userId: number,
  reason: string
): Promise<{ message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/users/${userId}/ban`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to ban user");
  }

  return response.json();
}

// ============== API Key Management ==============

export interface APIKey {
  id: number;
  key_id: string;
  key_prefix: string;
  name: string;
  requests_per_minute: number;
  images_per_day: number;
  is_active: boolean;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
}

export interface APIKeyCreate {
  name: string;
  requests_per_minute?: number;
  images_per_day?: number;
  expires_in_days?: number;
}

export interface APIKeyCreateResponse extends APIKey {
  secret_key: string;
  warning: string;
}

export interface APIKeyListResponse {
  keys: APIKey[];
  total: number;
}

export interface APIKeyUsage {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  total_images_generated: number;
  requests_today: number;
  images_today: number;
}

export async function listAPIKeys(): Promise<APIKeyListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/keys`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch API keys");
  }

  return response.json();
}

export async function createAPIKey(
  data: APIKeyCreate
): Promise<APIKeyCreateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/keys`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to create API key");
  }

  return response.json();
}

export async function revokeAPIKey(keyId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/keys/${keyId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to revoke API key");
  }
}

export async function reactivateAPIKey(keyId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/keys/${keyId}/reactivate`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to reactivate API key");
  }
}

export async function getAPIKeyUsage(keyId: string): Promise<APIKeyUsage> {
  const response = await fetch(`${API_BASE_URL}/api/keys/${keyId}/usage`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch API key usage");
  }

  return response.json();
}

export interface APIKeySecretResponse {
  key_id: string;
  secret_key: string;
  name: string;
}

export async function getAPIKeySecret(keyId: string): Promise<APIKeySecretResponse> {
  const response = await fetch(`${API_BASE_URL}/api/keys/${keyId}/secret`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch API key secret");
  }

  return response.json();
}
